import { colors, fonts, radius, spacing } from './theme';

const styles = {
  /* ─── App Shell ─── */
  appRoot: {
    /* RW-2552: the viewport floor + box-sizing live on `.reve-app-root`
       (index.css) — `100dvh` needs a stylesheet for its @supports fallback. */
    background: colors.white,
    backgroundImage: `
      radial-gradient(ellipse 90% 55% at 100% -10%, rgba(246,217,212,0.35) 0%, transparent 52%),
      radial-gradient(ellipse 70% 45% at -5% 105%, rgba(246,217,212,0.25) 0%, transparent 48%)
    `,
    color: colors.gray3,
    fontFamily: fonts.body,
    fontWeight: 400,
  },
  /* RW-2564: the login styles that used to live here — `authShell`, `loader`,
     `loginCard`, `loginLogo`, `loginTagline`, `loginSubtitle`, `googleButton`,
     `googleIconImg`, `hint` — are gone with the 360px card on a noir field.
     The split-screen sign in owns its own frame in components/AuthScreens.js,
     and its viewport floor is `.reve-auth-shell` in index.css (a class, so the
     `100dvh` @supports fallback survives — see the RW-2552 note there). */

  /* ─── Top Navigation ─── */
  topNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    padding: '0 20px',
    height: 72,
    minHeight: 72,
    borderBottom: `1px solid rgba(15,16,17,0.12)`,
    background: colors.noir,
    position: 'sticky',
    top: 0,
    zIndex: 50,
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  navLeft: { display: 'flex', alignItems: 'baseline', gap: 10 },
  brandMark: {
    fontSize: 28,
    fontWeight: 600,
    letterSpacing: '0.04em',
    fontFamily: fonts.heading,
    color: colors.white,
    textTransform: 'lowercase',
  },
  brandGold: {
    fontSize: 7,
    letterSpacing: 10,
    color: colors.coral,
    fontWeight: 500,
    fontFamily: fonts.heading,
    textTransform: 'uppercase',
  },
  navCenter: { display: 'flex', gap: spacing.xs, flex: 1, justifyContent: 'center', flexWrap: 'wrap' },
  navMobile: {
    display: 'flex',
    gap: spacing.xs,
    padding: '10px 16px',
    overflowX: 'auto',
    borderBottom: `1px solid rgba(255,255,255,0.08)`,
    background: colors.noir,
  },
  navLink: {
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: colors.white,
    fontSize: 14,
    fontWeight: 400,
    fontFamily: fonts.body,
    padding: '8px 14px 10px',
    borderRadius: 0,
    cursor: 'pointer',
  },
  navLinkMobile: {
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: colors.white,
    fontSize: 13,
    fontWeight: 400,
    fontFamily: fonts.body,
    padding: '8px 12px 10px',
    borderRadius: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  navLinkActive: {
    color: colors.white,
    fontWeight: 600,
    borderBottomColor: colors.coral,
    background: 'transparent',
  },
  navRight: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  iconBtn: {
    position: 'relative',
    background: 'transparent',
    border: `1px solid rgba(255,255,255,0.25)`,
    borderRadius: radius.sm,
    width: 36,
    height: 36,
    minHeight: 36,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: colors.white,
  },
  bell: { fontSize: 18 },
  // RW-1601: icon button for the WHITE slim header. `iconBtn` above was
  // designed for the retired dark header (white glyph + white-alpha border)
  // and rendered invisible ghosts on #fff — the Messages and Ask Rêve
  // buttons were there all along, white-on-white.
  headerIconBtn: {
    position: 'relative',
    background: 'transparent',
    border: '1px solid #E5E7EB',
    borderRadius: radius.sm,
    width: 36,
    height: 36,
    minHeight: 36,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: '#4B5563',
  },
  avatarWrap: { position: 'relative' },
  avatarBtn: {
    width: 36,
    height: 36,
    minHeight: 36,
    borderRadius: '50%',
    padding: 0,
    border: `2px solid ${colors.coral}`,
    overflow: 'hidden',
    cursor: 'pointer',
    background: colors.pearl,
    display: 'grid',
    placeItems: 'center',
  },
  navAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  navAvatarFallback: { fontSize: 16, fontWeight: 600, color: colors.purple },
  userMenu: {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 8px)',
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    padding: spacing.md,
    minWidth: 200,
    boxShadow: '0 8px 24px rgba(15,16,17,0.12)',
    zIndex: 100,
  },
  userMenuEmail: {
    margin: '0 0 10px 0',
    fontSize: 12,
    color: colors.gray1,
    wordBreak: 'break-all',
  },
  userMenuBtn: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: radius.sm,
    border: `1.5px solid ${colors.coral}`,
    background: 'transparent',
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: fonts.body,
  },

  /* ─── Layout ─── */
  mainLayout: {
    display: 'flex',
    maxWidth: 1200,
    margin: '0 auto',
    minHeight: 'calc(100vh - 72px)',
    alignItems: 'stretch',
  },
  sidebar: {
    display: 'flex',
    gap: spacing.sm,
    padding: '20px 16px',
    boxSizing: 'border-box',
    alignContent: 'flex-start',
    background: colors.pearl,
  },
  sidebarTitle: {
    width: '100%',
    margin: '0 0 8px 0',
    fontSize: 11,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: colors.noir,
    fontWeight: 600,
    fontFamily: fonts.heading,
  },
  filterBtn: {
    textAlign: 'left',
    padding: '10px 14px',
    borderRadius: radius.sm,
    border: `1px solid rgba(15,16,17,0.1)`,
    background: colors.pearl,
    color: colors.noir,
    fontSize: 14,
    fontWeight: 400,
    fontFamily: fonts.body,
    cursor: 'pointer',
  },
  filterBtnActive: {
    borderColor: colors.coral,
    color: colors.white,
    background: colors.coral,
    fontWeight: 600,
  },

  /* ─── Feed ─── */
  feedColumn: {
    flex: 1,
    padding: `20px 20px ${spacing.xxl}`,
    minWidth: 0,
    boxSizing: 'border-box',
    background: 'transparent', /* feed-reskin: warm canvas lives on .reve-feed-root; cards float */
  },
  composer: {
    background: colors.white,
    border: `1px solid rgba(15,16,17,0.10)`, /* feed-reskin: hairline neutral border */
    borderRadius: radius.sm, /* feed-reskin: editorial 4px corners */
    padding: spacing.lg,
    marginBottom: 20,
    boxShadow: '0 1px 2px rgba(15,16,17,0.06)',
  },
  composerTop: { display: 'flex', gap: 14, alignItems: 'flex-start' },
  composerAvatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    objectFit: 'cover',
    border: `2px solid ${colors.coral}`,
    flexShrink: 0,
  },
  composerAvatarFallback: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    background: colors.pearl,
    color: colors.noir,
    fontWeight: 600,
    fontSize: 18,
    fontFamily: fonts.heading,
    border: `2px solid ${colors.coral}`,
  },
  composerFields: { flex: 1, minWidth: 0 },
  composerInput: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: radius.sm,
    border: `1px solid ${colors.purpleLight}`,
    background: colors.white,
    color: colors.gray3,
    padding: '10px 12px',
    fontSize: 15,
    fontWeight: 400,
    fontFamily: fonts.body,
    resize: 'vertical',
    minHeight: 72,
    marginBottom: spacing.md,
    outline: 'none',
  },
  composerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  officeChips: { display: 'flex', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    fontSize: 12,
    padding: '6px 12px',
    borderRadius: radius.sm,
    border: `1px solid ${colors.pearl}`,
    background: colors.pearl,
    color: colors.noir,
    fontWeight: 400,
    fontFamily: fonts.body,
    cursor: 'pointer',
  },
  chipActive: {
    // Must restate the whole `border` shorthand, not just `borderColor`: `chip`
    // sets `border` and its value never changes between renders, so React skips
    // it and only removes the `borderColor` longhand on deselect — which drops
    // the pearl colour and falls back to the UA button border (RW-1969).
    border: '1px solid #6366F1',
    color: '#ffffff',
    background: '#6366F1',
    fontWeight: 600,
  },
  postBtn: {
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: 'none',
    background: colors.coral,
    color: colors.white,
    fontWeight: 500,
    fontSize: 14,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryBtn: {
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: `1.5px solid ${colors.coral}`,
    background: 'transparent',
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontSize: 14,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ghostBtn: {
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: `1px solid ${colors.pearl}`,
    background: 'transparent',
    color: colors.gray3,
    fontWeight: 400,
    fontSize: 14,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* ─── Post Cards ─── */
  feedList: { display: 'flex', flexDirection: 'column', gap: spacing.md },
  emptyFeed: {
    textAlign: 'center',
    color: colors.gray1,
    padding: `${spacing.xl} ${spacing.md}`,
    fontSize: 15,
    fontWeight: 400,
  },
  postCard: {
    background: colors.white,
    border: `1px solid rgba(15,16,17,0.10)`, /* feed-reskin: hairline neutral border (was pearl) */
    borderRadius: radius.sm, /* feed-reskin: editorial 4px corners */
    padding: spacing.lg,
    boxShadow: 'none', /* feed-reskin: float comes from warm canvas + hover lift (feed-polish.css) */
  },
  postHeader: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 },
  postAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    objectFit: 'cover',
    border: `2px solid ${colors.coral}`,
  },
  postAvatarFallback: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: colors.pearl,
    color: colors.noir,
    fontWeight: 600,
    fontFamily: fonts.heading,
    border: `2px solid ${colors.coral}`,
  },
  postMeta: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  postMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px 12px',
  },
  viewerBadge: {
    fontSize: 11,
    color: colors.gray1,
    fontWeight: 500,
    padding: '2px 8px',
    borderRadius: radius.sm,
    border: `1px solid ${colors.pearl}`,
    background: colors.pearl,
  },
  postAuthor: {
    fontWeight: 600,
    fontSize: 15,
    fontFamily: fonts.heading,
    color: colors.noir,
  },
  postTime: { fontSize: 13, color: colors.gray1, fontWeight: 400 },
  postText: {
    margin: '0 0 14px 0',
    fontSize: 15,
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: colors.gray3,
    fontWeight: 400,
  },
  postActions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    paddingTop: 12,
    borderTop: `1px solid rgba(15,16,17,0.10)`, /* feed-reskin: hairline neutral divider */
  },
  actionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: radius.sm,
    border: `1px solid ${colors.pearl}`,
    background: colors.white,
    color: colors.gray3,
    fontSize: 14,
    fontWeight: 400,
    fontFamily: fonts.body,
    cursor: 'pointer',
  },
  actionBtnActive: {
    borderColor: colors.coral,
    color: colors.white,
    background: colors.coral,
    fontWeight: 600,
  },
  commentStat: { fontSize: 13, color: colors.gray1, flex: 1, fontWeight: 400 },
  commentBtn: {
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: `1.5px solid ${colors.coral}`,
    background: 'transparent',
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontSize: 13,
    fontWeight: 500,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },

  /* ─── Comments ─── */
  /* RW-1021: tightened comment-thread spacing (was marginTop/paddingTop 14, form mb 12) */
  commentSection: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: `1px solid ${colors.pearl}`,
  },
  commentForm: { display: 'flex', flexDirection: 'column', gap: spacing.xs, marginBottom: 8 },
  typingIndicators: { marginTop: spacing.xs },
  typingLine: {
    margin: '4px 0 0 0',
    fontSize: 12,
    color: colors.gray1,
    fontStyle: 'italic',
  },
  typingName: { fontStyle: 'normal', fontWeight: 600, color: colors.purple },
  commentInput: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: radius.sm,
    border: `1px solid ${colors.purpleLight}`,
    background: colors.white,
    color: colors.gray3,
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 400,
    fontFamily: fonts.body,
    resize: 'vertical',
    outline: 'none',
  },
  commentSubmit: {
    alignSelf: 'flex-end',
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: `1.5px solid ${colors.coral}`,
    background: 'transparent',
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontSize: 13,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  commentList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  commentItem: {
    padding: '10px 12px',
    borderRadius: radius.sm,
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
  },
  commentAuthor: {
    fontWeight: 600,
    fontSize: 13,
    marginRight: 8,
    color: colors.purple,
    fontFamily: fonts.heading,
  },
  commentTime: { fontSize: 11, color: colors.gray1, fontWeight: 400 },
  commentText: {
    margin: '6px 0 0 0',
    fontSize: 14,
    lineHeight: 1.45,
    color: colors.gray3,
    fontWeight: 400,
  },

  /* ─── Priority Banners ─── */
  bannerImportant: {
    background: colors.yellow,
    color: colors.gray3,
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: 2,
    padding: '6px 14px',
    borderRadius: `${radius.md} ${radius.md} 0 0`,
    margin: `-${spacing.lg} -${spacing.lg} 14px -${spacing.lg}`,
    textAlign: 'center',
    fontFamily: fonts.heading,
  },
  bannerCritical: {
    background: colors.errorRed,
    color: colors.white,
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: 2,
    padding: '6px 14px',
    borderRadius: `${radius.md} ${radius.md} 0 0`,
    margin: `-${spacing.lg} -${spacing.lg} 14px -${spacing.lg}`,
    textAlign: 'center',
    fontFamily: fonts.heading,
  },
  chipImportant: {
    borderColor: colors.yellow,
    background: colors.yellow,
    color: colors.gray3,
  },
  chipCritical: {
    borderColor: colors.errorRed,
    background: colors.errorRed,
    color: colors.white,
  },

  /* ─── Acknowledge ─── */
  ackRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  ackBtn: {
    padding: '0 18px',
    height: 36,
    borderRadius: radius.sm,
    border: 'none',
    background: colors.coral,
    color: colors.white,
    fontWeight: 500,
    fontSize: 13,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  ackBtnDone: {
    padding: '0 18px',
    height: 36,
    borderRadius: radius.sm,
    border: 'none',
    background: '#4CAF50',
    color: colors.white,
    fontWeight: 500,
    fontSize: 13,
    fontFamily: fonts.body,
    cursor: 'default',
    opacity: 0.9,
    display: 'inline-flex',
    alignItems: 'center',
  },
  ackStat: {
    fontSize: 12,
    color: colors.gray1,
    fontWeight: 500,
    fontFamily: fonts.body,
  },

  /* ─── Notifications ─── */
  notifBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    background: colors.errorRed,
    color: colors.white,
    fontSize: 10,
    fontWeight: 700,
    borderRadius: '50%',
    minWidth: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    fontFamily: fonts.body,
  },
  notifDropdown: {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 8px)',
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    padding: 0,
    minWidth: 320,
    maxHeight: 400,
    overflowY: 'auto',
    boxShadow: '0 8px 24px rgba(15,16,17,0.12)',
    zIndex: 100,
  },
  notifTitle: {
    margin: 0,
    padding: '12px 16px',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: fonts.heading,
    color: colors.noir,
    borderBottom: `1px solid ${colors.pearl}`,
  },
  notifEmpty: {
    margin: 0,
    padding: '20px 16px',
    fontSize: 13,
    color: colors.gray1,
    textAlign: 'center',
    fontFamily: fonts.body,
  },
  notifItem: {
    display: 'flex',
    gap: 10,
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.pearl}`,
    cursor: 'pointer',
    alignItems: 'flex-start',
  },
  notifAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  notifContent: { flex: 1, minWidth: 0 },
  notifText: {
    margin: 0,
    fontSize: 13,
    color: colors.gray3,
    fontFamily: fonts.body,
    lineHeight: 1.4,
  },
  notifPreview: {
    margin: '4px 0 0 0',
    fontSize: 12,
    color: colors.gray1,
    fontFamily: fonts.body,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  notifTime: {
    margin: '4px 0 0 0',
    fontSize: 11,
    color: colors.gray1,
    fontFamily: fonts.body,
  },

  /* ─── People Directory ─── */
  peopleContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: `${spacing.lg} 20px ${spacing.xxl}`,
  },
  peopleSearch: {
    width: '100%',
    maxWidth: 400,
    boxSizing: 'border-box',
    padding: '10px 12px',
    borderRadius: radius.sm,
    border: `1px solid ${colors.purpleLight}`,
    background: colors.white,
    fontSize: 15,
    fontFamily: fonts.body,
    color: colors.gray3,
    marginBottom: spacing.lg,
    outline: 'none',
  },
  peopleGrid: { display: 'grid', gap: spacing.md },
  personCard: {
    display: 'flex',
    gap: 14,
    alignItems: 'center',
    padding: spacing.lg,
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    cursor: 'pointer',
  },
  personAvatar: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    objectFit: 'cover',
    border: `2px solid ${colors.coral}`,
    flexShrink: 0,
  },
  personAvatarFallback: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: colors.pearl,
    color: colors.noir,
    fontWeight: 600,
    fontSize: 20,
    fontFamily: fonts.heading,
    border: `2px solid ${colors.coral}`,
    flexShrink: 0,
  },
  personInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  personCardName: {
    fontWeight: 600,
    fontSize: 15,
    color: colors.noir,
    fontFamily: fonts.heading,
  },
  personCardOffice: { fontSize: 13, color: colors.gray1, fontFamily: fonts.body },
  personCardCount: {
    fontSize: 12,
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontFamily: fonts.body,
  },
  backBtn: {
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: `1.5px solid ${colors.coral}`,
    background: 'transparent',
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontSize: 14,
    fontFamily: fonts.body,
    cursor: 'pointer',
    marginBottom: 20,
    display: 'inline-flex',
    alignItems: 'center',
  },
  personHeader: {
    display: 'flex',
    gap: 20,
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  personLargeAvatar: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    objectFit: 'cover',
    border: `3px solid ${colors.coral}`,
  },
  personLargeAvatarFallback: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: colors.pearl,
    color: colors.noir,
    fontWeight: 600,
    fontSize: 32,
    fontFamily: fonts.heading,
    border: `3px solid ${colors.coral}`,
  },
  personName: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600,
    color: colors.noir,
    fontFamily: fonts.heading,
  },
  personEmail: {
    margin: '4px 0 0 0',
    fontSize: 13,
    color: colors.gray1,
    fontFamily: fonts.body,
  },
  personOffice: {
    margin: '2px 0 0 0',
    fontSize: 13,
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontFamily: fonts.body,
  },
  personPostCount: {
    margin: '0 0 16px 0',
    fontSize: 14,
    color: colors.gray3,
    fontWeight: 500,
    fontFamily: fonts.body,
  },

  /* ─── Recognition ─── */
  recognitionCard: {
    background: colors.pearlLight,
    borderLeft: `3px solid ${colors.coral}`,
    borderRadius: radius.md,
    padding: spacing.lg,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  recognitionBadge: {
    display: 'inline-block',
    background: colors.coral,
    color: colors.white,
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: radius.sm,
    marginBottom: spacing.sm,
    fontFamily: fonts.heading,
    textTransform: 'uppercase',
  },
  recognitionMessage: {
    margin: '8px 0 0 0',
    fontSize: 15,
    fontStyle: 'italic',
    color: colors.gray3,
    lineHeight: 1.5,
    fontFamily: fonts.body,
  },
  recognitionFrom: {
    margin: '8px 0 0 0',
    fontSize: 12,
    color: colors.gray1,
    fontFamily: fonts.body,
  },
  badgePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: radius.sm,
    background: colors.pearl,
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontSize: 11,
    fontWeight: 600,
    fontFamily: fonts.body,
    marginRight: 6,
    marginBottom: 4,
  },

  /* ─── Events ─── */
  eventsContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: `${spacing.lg} 20px ${spacing.xxl}`,
  },
  eventsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
    gap: 12,
  },
  eventsTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600,
    color: colors.noir,
    fontFamily: fonts.heading,
  },
  eventForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    padding: spacing.lg,
    marginBottom: spacing.lg,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  eventInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    borderRadius: radius.sm,
    border: `1px solid ${colors.purpleLight}`,
    background: colors.white,
    fontSize: 14,
    fontFamily: fonts.body,
    color: colors.gray3,
    outline: 'none',
  },
  eventCard: {
    display: 'flex',
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  eventDateBadge: {
    background: colors.coral,
    color: colors.white,
    fontWeight: 700,
    fontSize: 14,
    fontFamily: fonts.heading,
    padding: '16px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 70,
    textAlign: 'center',
    lineHeight: 1.3,
  },
  eventCardBody: {
    flex: 1,
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  eventCardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: colors.noir,
    fontFamily: fonts.heading,
  },
  eventCardMeta: { margin: 0, fontSize: 13, color: colors.gray1, fontFamily: fonts.body },
  eventCardOffice: {
    margin: 0,
    fontSize: 12,
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontFamily: fonts.body,
  },
  eventCardDesc: {
    margin: '4px 0 0 0',
    fontSize: 13,
    color: colors.gray3,
    fontFamily: fonts.body,
    lineHeight: 1.4,
  },
  eventActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: spacing.sm,
    flexWrap: 'wrap',
  },
  rsvpBtn: {
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: `1.5px solid ${colors.coral}`,
    background: 'transparent',
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontSize: 13,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  rsvpBtnActive: {
    padding: '0 16px',
    height: 36,
    borderRadius: radius.sm,
    border: '1.5px solid #4CAF50',
    background: '#4CAF50',
    color: colors.white,
    fontWeight: 500,
    fontSize: 13,
    fontFamily: fonts.body,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  eventAttendeeStat: { fontSize: 12, color: colors.gray1, fontFamily: fonts.body },
  calLink: {
    fontSize: 12,
    color: colors.purple,
    fontWeight: 500,
    fontFamily: fonts.body,
    textDecoration: 'none',
    marginLeft: 'auto',
  },

  /* ─── Documents ─── */
  docsContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: `${spacing.lg} 20px ${spacing.xxl}`,
  },
  docsFolderBar: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  docsFolderBtn: {
    padding: '10px 18px',
    borderRadius: radius.sm,
    border: `1px solid ${colors.pearl}`,
    background: colors.white,
    color: colors.gray3,
    fontSize: 14,
    fontWeight: 400,
    fontFamily: fonts.body,
    cursor: 'pointer',
  },
  docsFolderBtnActive: {
    // RW-1971: restate the whole shorthand — a longhand-only override is
    // removed on deselect without the base `border` being rewritten (RW-1969).
    border: `1px solid ${colors.coral}`,
    background: colors.coral,
    color: colors.white,
    fontWeight: 600,
  },
  uploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  fileInput: { fontSize: 14, fontFamily: fonts.body, color: colors.gray3 },
  uploadProgress: {
    position: 'relative',
    width: 200,
    height: 24,
    background: colors.pearl,
    borderRadius: radius.sm,
    overflow: 'hidden',
  },
  uploadProgressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: colors.coral,
    borderRadius: radius.sm,
    transition: 'width 0.2s',
  },
  uploadProgressText: {
    position: 'relative',
    zIndex: 1,
    lineHeight: '24px',
    fontSize: 12,
    fontWeight: 600,
    color: colors.noir,
    textAlign: 'center',
    display: 'block',
    fontFamily: fonts.body,
  },
  docsTable: { display: 'flex', flexDirection: 'column', gap: spacing.sm },
  docRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  docIcon: { fontSize: 24, flexShrink: 0 },
  docInfo: { flex: 1, minWidth: 0 },
  docName: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.purple,
    fontFamily: fonts.body,
    textDecoration: 'none',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  docMeta: {
    display: 'flex',
    gap: 12,
    marginTop: 4,
    fontSize: 12,
    color: colors.gray1,
    fontFamily: fonts.body,
    flexWrap: 'wrap',
  },
  docDownload: {
    padding: '0 14px',
    height: 32,
    borderRadius: radius.sm,
    border: `1.5px solid ${colors.coral}`,
    background: 'transparent',
    color: colors.coralInk, // RW-2960: AA text; the border stays coral
    fontWeight: 500,
    fontSize: 12,
    fontFamily: fonts.body,
    textDecoration: 'none',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },

  /* ─── Photo Albums ─── */
  photosContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: `${spacing.lg} 20px ${spacing.xxl}`,
  },
  albumGrid: { display: 'grid', gap: spacing.md },
  albumCard: {
    position: 'relative',
    borderRadius: radius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    border: `1px solid ${colors.pearl}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    background: colors.pearl,
    aspectRatio: '4/3',
  },
  albumCover: { width: '100%', height: '100%', objectFit: 'cover' },
  albumOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(15,16,17,0.8))',
    padding: `${spacing.lg} 14px 14px`,
    color: colors.white,
  },
  albumTitle: { margin: 0, fontSize: 15, fontWeight: 600, fontFamily: fonts.heading },
  albumMeta: { margin: '4px 0 0 0', fontSize: 12, opacity: 0.85, fontFamily: fonts.body },
  lightboxOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15,16,17,0.95)',
    zIndex: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lightboxClose: {
    position: 'absolute',
    top: 16,
    right: 20,
    background: 'transparent',
    border: 'none',
    color: colors.white,
    fontSize: 28,
    cursor: 'pointer',
    fontFamily: fonts.body,
    zIndex: 201,
  },
  lightboxImg: {
    maxWidth: '90vw',
    maxHeight: '80vh',
    objectFit: 'contain',
    borderRadius: radius.md,
  },
  lightboxNav: {
    background: 'rgba(255,255,255,0.15)',
    border: 'none',
    color: colors.white,
    fontSize: 32,
    padding: '8px 16px',
    cursor: 'pointer',
    borderRadius: radius.sm,
  },
  lightboxCounter: { color: colors.white, fontSize: 14, marginTop: 12, fontFamily: fonts.body },

  /* ─── Onboarding ─── */
  checklistContainer: {
    maxWidth: 800,
    margin: '0 auto',
    padding: `${spacing.lg} 20px ${spacing.xxl}`,
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    marginBottom: spacing.sm,
  },
  checklistCheckbox: {
    width: 20,
    height: 20,
    accentColor: colors.coral,
    cursor: 'pointer',
    flexShrink: 0,
  },
  progressBarOuter: {
    width: '100%',
    height: 8,
    background: colors.pearl,
    borderRadius: radius.sm,
    overflow: 'hidden',
    marginBottom: spacing.md,
  },
  progressBarInner: {
    height: '100%',
    background: colors.coral,
    borderRadius: radius.sm,
    transition: 'width 0.3s',
  },

  /* ─── Training ─── */
  trainingContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: `${spacing.lg} 20px ${spacing.xxl}`,
  },
  trainingCard: {
    padding: spacing.md,
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  // RW-1971: full shorthand, not borderColor — see docsFolderBtnActive.
  overdue: { border: `1px solid ${colors.errorRed}`, borderLeftWidth: 4 },

  /* ─── Admin ─── */
  adminContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: `${spacing.lg} 20px ${spacing.xxl}`,
  },
  adminSection: { marginBottom: spacing.xl },
  adminSectionTitle: {
    margin: '0 0 16px 0',
    fontSize: 18,
    fontWeight: 600,
    color: colors.noir,
    fontFamily: fonts.heading,
  },
  statGrid: { display: 'grid', gap: spacing.md, marginBottom: spacing.lg },
  statCard: {
    padding: 20,
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  statNumber: {
    fontSize: 28,
    fontWeight: 700,
    color: '#111827',
    fontFamily: fonts.heading,
    margin: 0,
  },
  statLabel: {
    fontSize: 12,
    color: colors.gray1,
    fontFamily: fonts.body,
    margin: '4px 0 0 0',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  tableWrap: { overflowX: 'auto' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: fonts.body,
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    borderBottom: `2px solid ${colors.pearl}`,
    color: colors.noir,
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 14px',
    borderBottom: `1px solid ${colors.pearl}`,
    color: colors.gray3,
    verticalAlign: 'middle',
  },

  /* ─── Search ─── */
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchInput: {
    width: 220,
    padding: '8px 12px 8px 12px',
    borderRadius: radius.sm,
    border: `1px solid ${colors.purpleLight}`,
    background: colors.white,
    color: colors.gray3,
    fontSize: 13,
    fontFamily: fonts.body,
    outline: 'none',
  },
  searchIcon: {
    position: 'absolute',
    left: 8,
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    pointerEvents: 'none',
  },
  searchResults: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    background: colors.white,
    border: `1px solid ${colors.pearl}`,
    borderRadius: radius.md,
    minWidth: 340,
    maxHeight: 400,
    overflowY: 'auto',
    boxShadow: '0 8px 24px rgba(15,16,17,0.12)',
    zIndex: 100,
  },
  searchGroup: {
    padding: '8px 16px 4px',
    fontSize: 11,
    fontWeight: 600,
    color: colors.gray1,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontFamily: fonts.body,
  },
  searchItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.pearl}`,
    fontSize: 13,
    color: colors.gray3,
    fontFamily: fonts.body,
    background: 'transparent',
    border: 'none',
    borderBlockEnd: `1px solid ${colors.pearl}`,
    width: '100%',
    textAlign: 'left',
  },
  searchItemIcon: {
    fontSize: 16,
    flexShrink: 0,
    width: 24,
    textAlign: 'center',
  },
};

export default styles;
